import { Outlet } from 'react-router-dom'

import { useCallback, useState } from 'react'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { PurchaseOrdersPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import type { ResponsivePagePrimaryAction } from 'modules/layout/components/ResponsivePage/_PageHeader/types'
import WithPaywall from 'modules/procurement/components/WithPaywall'
import PurchaseOrdersDataTable from 'modules/procurement/components/purchaseOrders/PurchaseOrdersDataTable'

export interface PurchaseOrdersOutletContext {
  visibleV2Ids: string[]
}

const PurchaseOrdersPage = () => {
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  // Ordered V2 IDs visible in the data table — feeds sidepanel prev/next navigation.
  const [visibleV2Ids, setVisibleV2Ids] = useState<string[]>([])
  const handleVisibleV2IdsChange = useCallback((ids: string[]) => {
    setVisibleV2Ids((prev) =>
      prev.length === ids.length && prev.every((id, i) => id === ids[i]) ? prev : ids
    )
  }, [])

  const { loading, data, error } = useQuery(PurchaseOrdersPageDocument, {
    variables: { companyId: company.id },
  })

  useEffect(() => {
    track(trackingEvents.procurement.openPurchaseOrders)
  }, [])

  const canReadPurchaseOrder = data?.procurement?._policies?.purchaseOrderRead.can ?? false
  const canManageProcurementSettings = data?.procurement?._policies?.settingsManage.can ?? false
  const isPoTemplatesEnabled = !!data?.features?.poTemplatesFeature?.edges?.some(
    (e) => e.node.name === 'dev_procurement_po_templates' && e.node.enabled
  )

  const actions: ResponsivePagePrimaryAction[] = canManageProcurementSettings
    ? [
        {
          icon: 'settings',
          to: navigation.settings.procurement.fullPath(),
          label: '',
          variant: 'secondary',
        },
      ]
    : []

  return (
    <WithPaywall
      allowWhileLoading
      features={[Features.procurement_purchase_orders]}
      modules={[AvailableModules.procurement]}
    >
      {() => (
        <ResponsivePage.Body
          variant="full"
          actions={{ primary: actions }}
          authorized={canReadPurchaseOrder}
          error={error}
          loading={loading}
        >
          <PurchaseOrdersDataTable
            canExport={isPoTemplatesEnabled}
            onVisibleV2IdsChange={handleVisibleV2IdsChange}
          />
          <Outlet context={{ visibleV2Ids }} />
        </ResponsivePage.Body>
      )}
    </WithPaywall>
  )
}

export default PurchaseOrdersPage
